
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscriptionService } from "@/services/subscriptionService";

export const useVideoSubmission = () => {
  const { toast } = useToast();
  const { canProcessVideo, incrementVideoUsage } = useSubscriptionService();

  const handleVideoSubmit = async (videoId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Fetch video details using Supabase Edge Function
      const { data: videoDetails, error: videoDetailsError } = await supabase.functions.invoke('get-youtube-video-details', {
        body: { videoId }
      });

      if (videoDetailsError) {
        console.error('Error fetching video details:', videoDetailsError);
        toast({
          title: "Error",
          description: "Failed to fetch video details",
          variant: "destructive",
        });
        return false;
      }

      // Check if user has reached their limits
      // Convert duration from PT1H2M3S format to minutes
      const durationString = videoDetails?.duration || "PT0M0S";
      const durationMinutes = parseDuration(durationString);
      
      // Check if user can process this video based on their subscription
      const canProcess = await canProcessVideo(durationMinutes);
      if (!canProcess) {
        return false;
      }

      // Use upsert operation to handle duplicate entries
      const { error: upsertError } = await supabase
        .from("video_history")
        .upsert(
          {
            user_id: user.id,
            video_id: videoId,
            video_title: videoDetails?.title || null,
            video_thumbnail: videoDetails?.thumbnail || null,
          },
          {
            onConflict: 'user_id,video_id',
            ignoreDuplicates: false
          }
        );

      if (upsertError) {
        console.error('Error storing video history:', upsertError);
        if (upsertError.code === '23505') {
          toast({
            description: "This video is already in your history",
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to store video in history",
            variant: "destructive",
          });
        }
        return false;
      }

      // Increment video usage count for today
      await incrementVideoUsage();

      return true;
    } catch (error) {
      console.error("Error handling video submission:", error);
      toast({
        title: "Error",
        description: "Failed to process video",
        variant: "destructive",
      });
      return false;
    }
  };

  // Helper function to parse ISO 8601 duration to minutes
  const parseDuration = (duration: string): number => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    
    if (!match) {
      return 0;
    }
    
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    
    return hours * 60 + minutes + Math.ceil(seconds / 60);
  };

  return { handleVideoSubmit };
};
